// @flow

import styles from './CompanySuggestion.less';
import React from 'react';
import { omit } from 'lodash';
import addCommas from 'add-commas';
import NavLink from 'src/components/NavLink/NavLink';
import { Paragraph, TextLink, Text } from 'seek-style-guide/react';

type Props = {|
  companySuggestion: ?SearchViewModelCompanySuggestion,
  searchParams?: ?SearchParams
|};

const createCompanySuggestionLink = ({ companySuggestion, searchParams }) => {
  if (!companySuggestion) {
    return null;
  }

  const { count, search } = companySuggestion;
  const { companyName, keywords } = search || {};

  if (keywords) {
    return {
      location: {
        pathname: '/jobs',
        query: { ...omit(searchParams, 'companysearch'), keywords }
      },
      text: `Show ${addCommas(count)} jobs containing “${keywords}”`,
      analytics: 'serp-broadened-company-click',
      dataAutomationId: 'company-serp-broad'
    };
  }

  if (companyName) {
    if (count === '0') {
      return {
        text: `0 jobs at ${companyName}`,
        dataAutomationId: 'company-serp-exact',
        location: null,
        analytics: null
      };
    }

    return {
      location: {
        pathname: '/jobs',
        query: { ...searchParams, keywords: companyName, companysearch: true }
      },
      text: `Show ${addCommas(count)} jobs at ${companyName}`,
      analytics: 'serp-exact-company-click',
      dataAutomationId: 'company-serp-exact'
    };
  }

  return null;
};

const CompanySuggestion = ({ companySuggestion, searchParams }: Props) => {
  const model = createCompanySuggestionLink({
    companySuggestion,
    searchParams
  });

  if (!model) {
    return null;
  }

  const { location, text, analytics, dataAutomationId } = model;

  return (
    <Paragraph>
      {location ? (
        <NavLink
          component={TextLink}
          className={styles.block}
          location={location}
          analytics={analytics}
        >
          <Text small data-automation={dataAutomationId}>
            {text}
          </Text>
        </NavLink>
      ) : (
        <Text small secondary data-automation={dataAutomationId}>
          {text}
        </Text>
      )}
    </Paragraph>
  );
};

export default CompanySuggestion;
